/**
 * Maquinas de estado del PMIS.
 * Reflejan exactamente la Declaracion de Alcance VD secciones 5 y 12.6.
 */

// ===== Budget (ano fiscal Tecpetrol) =====
export const ESTADOS_BUDGET = ["FUTURO", "ACTIVO", "CERRADO"] as const;
export type EstadoBudget = (typeof ESTADOS_BUDGET)[number];

// ===== AFE (Declaracion VD seccion 5.2) =====
export const ESTADOS_AFE = [
  "BORRADOR",
  "CREADO_NO_APROBADO",
  "EN_REVISION_JF",
  "APROBADO",
  "EN_EJECUCION",
  "EXTENDIDO",
  "CERRADO",
  "CANCELADO",
] as const;
export type EstadoAfe = (typeof ESTADOS_AFE)[number];

export const TRANSICIONES_AFE: Record<EstadoAfe, EstadoAfe[]> = {
  BORRADOR: ["CREADO_NO_APROBADO", "CANCELADO"],
  CREADO_NO_APROBADO: ["EN_REVISION_JF", "BORRADOR", "CANCELADO"],
  // JEFE aprueba o rechaza. Editor PMIS puede aprobar en respaldo (auditado).
  EN_REVISION_JF: ["APROBADO", "BORRADOR", "EXTENDIDO", "CERRADO", "CANCELADO"],
  // Al crear primera PR/PO/SO el sistema transita automaticamente a EN_EJECUCION.
  APROBADO: ["EN_EJECUCION", "EN_REVISION_JF", "CANCELADO"],
  EN_EJECUCION: ["EN_REVISION_JF", "EXTENDIDO", "CERRADO"],
  EXTENDIDO: ["EN_EJECUCION", "EN_REVISION_JF", "CERRADO"],
  CERRADO: ["EN_EJECUCION"], // solo Editor PMIS, con badge
  CANCELADO: [],
};

// ===== PR (Decl. Alcance VD §5.3) =====
export const ESTADOS_PR = [
  "CREADA_NO_APROBADA",
  "APROBADA",
  "ET_FAC",
  "EE_ABAS",
  "ADJ_PARCIAL",
  "ADJ_TOTAL",
  "CANCELADA",
] as const;
export type EstadoPr = (typeof ESTADOS_PR)[number];

export const TRANSICIONES_PR: Record<EstadoPr, EstadoPr[]> = {
  // PL/PL2/CFE crea PR
  CREADA_NO_APROBADA: ["APROBADA", "CANCELADA"],
  // Gerencia (PL) aprueba la PR para abrir el proceso de Abastecimientos
  APROBADA: ["ET_FAC", "CANCELADA"],
  // Etapa con FAC (Facilities)
  ET_FAC: ["EE_ABAS", "CANCELADA"],
  // Pasa a evaluacion economica de Abastecimientos
  EE_ABAS: ["ADJ_PARCIAL", "ADJ_TOTAL", "CANCELADA"],
  // Una primera PO emitida (saldo no adjudicado sigue como compromiso)
  ADJ_PARCIAL: ["ADJ_TOTAL", "CANCELADA"],
  // Todas las POs emitidas — la PR "desaparece" como compromiso (VD §7.4)
  ADJ_TOTAL: [],
  CANCELADA: [],
};

// ===== PO (Decl. Alcance VD §5.4) =====
export const ESTADOS_PO = [
  "EMITIDA",
  "EN_ESPERA_ENTREGA",
  "ENTREGADA",
  "PAGADA",
  "VENCIDA",
  "CANCELADA",
] as const;
export type EstadoPo = (typeof ESTADOS_PO)[number];

export const TRANSICIONES_PO: Record<EstadoPo, EstadoPo[]> = {
  // ABAS/PL emite la PO. Punto de inicio del plazo (VD §A5)
  EMITIDA: ["EN_ESPERA_ENTREGA", "CANCELADA"],
  // Sistema marca esperando entrega mientras fechaEntregaCalc no se haya cumplido
  EN_ESPERA_ENTREGA: ["ENTREGADA", "VENCIDA", "CANCELADA"],
  // Registro de fecha real de entrega
  ENTREGADA: ["PAGADA"],
  // Registro de pago
  PAGADA: [],
  // Vencida sin entrega registrada
  VENCIDA: ["ENTREGADA", "CANCELADA"],
  CANCELADA: [],
};

// ===== SO (VD §5.5 — modelo simplificado a 5 estados + sub-flags) =====
//
// Modelo reducido por pragmatismo operativo. NO existe un estado de
// "ejecución": una vez la SO queda APROBADA, la línea base está congelada y
// la SO queda habilitada para el reporte diario. Los matices se manejan como
// sub-flags en la entidad So:
//
//   - nivelRevision: "PL2" | "PL"   (solo en EN_REVISION)
//   - enReprogramacion: boolean     marca una SO que ya estuvo APROBADA y el
//                                   PL2 devolvió a EN_REVISION para modificar
//                                   la línea base (reaprobación).
//
// El historial mantiene la trazabilidad detallada (rechazos, escalamientos).
//
export const ESTADOS_SO = [
  "EN_COTIZACION",   // Contratista llenando o corrigiendo el PDT
  "EN_REVISION",     // Tecpetrol revisando (nivelRevision indica quién)
  "APROBADA",        // Línea base congelada — habilitada para reporte diario
  "CERRADA",         // SO bloqueada, dossier entregado
  "CANCELADA",       // Cancelada con motivo
] as const;
export type EstadoSo = (typeof ESTADOS_SO)[number];

// Transiciones permitidas (origen → destinos válidos).
// APROBADA → EN_REVISION: el PL2 solicita modificar la línea base congelada
// (reprogramación); la reaprobación la autoriza quien tenga límite suficiente.
export const TRANSICIONES_SO: Record<EstadoSo, EstadoSo[]> = {
  EN_COTIZACION: ["EN_REVISION", "CANCELADA"],
  EN_REVISION: ["EN_COTIZACION", "APROBADA", "CANCELADA"],
  APROBADA: ["EN_REVISION", "CERRADA", "CANCELADA"],
  CERRADA: [],
  CANCELADA: [],
};

// ===== RD - Reporte Diario (modelo simplificado VD §5.6) =====
//
// 3 estados oficiales + sub-flag `nivelRevision: "INT" | "PL"`.
// El rechazo siempre devuelve a BORRADOR; el historial conserva quién rechazó.
//
//   BORRADOR      ────envía────▶ EN_REVISION (nivelRevision=INT)
//                                    │  INT aprueba (sub-flag → "PL")
//                                    │
//                                    │  PL aprueba ───▶ APROBADO (datos firmes)
//                                    │
//                                    └─ rechaza ───────▶ BORRADOR (loop)
//
export const ESTADOS_RD = [
  "BORRADOR",     // Contratista trabajando o corrigiendo tras rechazo
  "EN_REVISION",  // INT o PL revisando (nivelRevision indica quién)
  "APROBADO",     // PL/PL2 aprobó; datos cargados a BD; KPIs actualizados
  "CANCELADO",    // RD descartado (raro; solo por administración)
] as const;
export type EstadoRd = (typeof ESTADOS_RD)[number];

export const TRANSICIONES_RD: Record<EstadoRd, EstadoRd[]> = {
  BORRADOR: ["EN_REVISION", "CANCELADO"],
  EN_REVISION: ["BORRADOR", "APROBADO", "CANCELADO"],
  // APROBADO → BORRADOR: SOLO el PL2 habilita la re-edición (flujo canónico
  // pt. 12-13). El repo limpia firma/fechaIntegracion para que al re-aprobar
  // los valores se recalculen sin doble carga.
  APROBADO: ["BORRADOR"],
  CANCELADO: [],
};

// ===== Acta de Cantidades =====
export const ESTADOS_ACTA = [
  "BORRADOR",
  "EN_REVISION_INT",
  "APROBADA_INT",
  "EN_REVISION_PL2",
  "APROBADA",
  "RECHAZADA",
  "FACTURADA",
  // Terminal. RECHAZADA no sirve para esto: no es terminal, vuelve a BORRADOR
  // ("devuélvala y corríjala"). ANULADA es "esta acta no debió existir": el
  // documento se conserva con sus firmas como evidencia, pero deja de contar.
  "ANULADA",
] as const;
export type EstadoActa = (typeof ESTADOS_ACTA)[number];

// ===== Contrato Marco =====
export const ESTADOS_CONTRATO = [
  "BORRADOR",
  "EN_APROBACION",
  "VIGENTE",
  "VENCIDO",
  "TERMINADO",
] as const;
export type EstadoContrato = (typeof ESTADOS_CONTRATO)[number];

/** Helper: valida si una transicion es permitida. */
export function puedeTransicionar<T extends string>(
  estadoActual: T,
  estadoDestino: T,
  transiciones: Record<T, readonly T[]>,
): boolean {
  return transiciones[estadoActual]?.includes(estadoDestino) ?? false;
}
